import { useSyncExternalStore } from 'react';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { WorkTaskTypeCatalogItem } from 'api-client';
import { CreateWorkFormValues } from 'components/work/create-work/types';

/**
 * Durable outbox for work-task submissions. Every create-work flow (inspection,
 * tree-details, inventory, inline) enqueues one entry per work card here on submit,
 * then navigates optimistically. An app-wide runner drains the outbox through an
 * ordered, resumable pipeline so the whole chain survives loss of connection and app
 * restarts:
 *
 *   awaiting-host → (host submit acked)         gate 1
 *   creating      → create the work task (asset tp2AssetId is now known: up front for
 *                   inspection/tree/inline, from the createAsset response for inventory)
 *   reconciling   → (tp2WorkRecordId assigned)  gate 3a
 *                 → (photos uploaded) link them  gate 3b
 *                 → (photos attached) done       gate 3c
 *
 * Entries hold only METADATA (a few KB) — the heavy photo bytes live in the upload
 * cache + the persisted TanStack upload mutation — so localStorage (tenant-scoped, with
 * a 7-day TTL) is sufficient and keeps snapshot reads synchronous for React.
 *
 * Give-up has two axes: a submission that keeps failing while ONLINE is dropped after a
 * few attempts (see {@link bumpWorkSubmissionAttempts}); one that simply never got a
 * chance (long offline) is bounded by the TTL. Offline waiting never counts as an
 * attempt, so a queued-but-offline submission isn't penalized.
 */

const KEY_PREFIX = 'workSubmissionOutbox';
export const WORK_SUBMISSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type WorkSubmissionHostKind = 'inspection' | 'tree' | 'inventory' | 'inline';
/** Terminal outcome of a photo's upload. Recorded durably keyed by photoId (see the photo
 *  upload-outcome store below), NOT on the submission — a photo is often uploaded during
 *  form-fill, before the submission that references it exists, so the entry can't be the
 *  source of truth for upload status. The runner reads the outcome by photoId at reconcile. */
export type PhotoUploadOutcome = 'uploaded' | 'failed';
export type WorkSubmissionPhase = 'awaiting-host' | 'creating' | 'reconciling';

export interface WorkSubmissionPhoto {
  photoId: string;
  fileName: string;
}

export interface WorkSubmission {
  /** Client-minted id the work task will be created with. Primary key of the entry. */
  workTaskId: string;
  /** Batch id linking these work entries to their host submit; null for inline (no host). */
  submissionId: string | null;
  hostKind: WorkSubmissionHostKind;
  createdAt: number;
  /** Gate 1: the host submit (inspection/asset) has been acknowledged. Inline starts true. */
  hostDone: boolean;
  /** Asset context: known up front (inspection/tree/inline) or resolved from the host
   *  response (inventory, via markSubmissionHostDone). */
  assetId: string | null;
  tp2AssetId: number | null;
  /** Inputs the runner uses to build the create-work payload once gates 1–2 pass. */
  values: CreateWorkFormValues;
  taskType: WorkTaskTypeCatalogItem;
  /** The create-work mutation has been acknowledged. */
  workCreated: boolean;
  /** Photos to attach. Each photo's upload outcome is looked up by id from the outcome
   *  store — the entry only records which photos belong to it, not their upload status. */
  photos: WorkSubmissionPhoto[];
  /** linkPhotosToWorkTask has been issued for the uploaded photos. */
  photosLinked: boolean;
  /** Count of failed *online* reconciliation passes; the runner gives up past a max.
   *  Offline waiting doesn't count (it never attempts), so it isn't penalized. */
  attempts: number;
  /** Last error message (for the failure toast); set when a step gives up. */
  lastError?: string;
}

const listeners = new Set<() => void>();

/** Which step of the chain a submission permanently failed at (for the failure toast). */
export type WorkSubmissionFailureStep = 'host' | 'asset' | 'work' | 'photos';

export interface WorkSubmissionFailure {
  workTaskId: string;
  hostKind: WorkSubmissionHostKind;
  tp2AssetId: number | null;
  step: WorkSubmissionFailureStep;
  message: string;
}

const failureListeners = new Set<(failure: WorkSubmissionFailure) => void>();

/**
 * Subscribe to permanent give-ups (host submit, create-work, or photo-link exhausted its
 * retries). The UI shows a toast naming the failed step + the tp2 asset id. Lives here —
 * not in the runner — so the host mutations can emit host-step failures without importing
 * the runner's graph.
 */
export function subscribeWorkSubmissionFailures(
  listener: (failure: WorkSubmissionFailure) => void
): () => void {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
}

export function emitWorkSubmissionFailure(failure: WorkSubmissionFailure): void {
  failureListeners.forEach((listener) => listener(failure));
}

function storageKey(): string {
  return `${KEY_PREFIX}:${TenantRepository.getCurrentTenantPid() ?? 'unknown'}`;
}

function readAll(key: string): WorkSubmission[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as WorkSubmission[]) : [];
  } catch {
    return [];
  }
}

function writeAll(key: string, entries: WorkSubmission[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(entries));
  } catch {
    // Best-effort: a full/unavailable localStorage must not break submitting.
  }
  listeners.forEach((listener) => listener());
}

function prune(entries: WorkSubmission[], now: number): WorkSubmission[] {
  return entries.filter((entry) => now - entry.createdAt < WORK_SUBMISSION_TTL_MS);
}

function submissionsForKey(key: string, now: number): WorkSubmission[] {
  return prune(readAll(key), now);
}

/** All pending work submissions for the current tenant (for the runner). */
export function getWorkSubmissions(now: number = Date.now()): WorkSubmission[] {
  return submissionsForKey(storageKey(), now);
}

/** Derived pipeline phase for an entry. */
export function workSubmissionPhase(entry: WorkSubmission): WorkSubmissionPhase {
  if (!entry.hostDone) return 'awaiting-host';
  if (!entry.workCreated) return 'creating';
  return 'reconciling';
}

/**
 * Enqueue one work submission. `hostDone` is true for the inline flow (no host step);
 * for the three form flows it starts false and is flipped by the host mutation's
 * success (see {@link markSubmissionHostDone}).
 */
export function enqueueWorkSubmission(entry: WorkSubmission, now: number = Date.now()): void {
  const key = storageKey();
  const entries = prune(readAll(key), now);
  if (entries.some((e) => e.workTaskId === entry.workTaskId)) return;
  writeAll(key, [...entries, entry]);
}

function updateEntry(
  predicate: (entry: WorkSubmission) => boolean,
  patch: (entry: WorkSubmission) => WorkSubmission,
  now: number
): void {
  const key = storageKey();
  let changed = false;
  const next = prune(readAll(key), now).map((entry) => {
    if (!predicate(entry)) return entry;
    changed = true;
    return patch(entry);
  });
  if (changed) writeAll(key, next);
}

/** Gate 1: the host submit for `submissionId` acked — resolve asset ids and unblock. */
export function markSubmissionHostDone(
  submissionId: string,
  resolved: { assetId?: string; tp2AssetId?: number },
  now: number = Date.now()
): void {
  updateEntry(
    (entry) => entry.submissionId === submissionId,
    (entry) => ({
      ...entry,
      hostDone: true,
      assetId: resolved.assetId ?? entry.assetId,
      tp2AssetId: resolved.tp2AssetId ?? entry.tp2AssetId,
    }),
    now
  );
}

/**
 * The host submit for `submissionId` permanently failed: toast (step "host") and discard
 * its still-queued work entries. Entries already past the host gate are left alone — their
 * work was created against a committed host, so a late host error doesn't unwind them.
 */
export function failWorkSubmissionsForHost(
  submissionId: string,
  message: string,
  now: number = Date.now()
): void {
  const key = storageKey();
  const entries = prune(readAll(key), now);
  const isBlocked = (entry: WorkSubmission) =>
    entry.submissionId === submissionId && !entry.hostDone;
  const affected = entries.filter(isBlocked);
  if (affected.length === 0) return;
  affected.forEach((entry) => {
    emitWorkSubmissionFailure({
      workTaskId: entry.workTaskId,
      hostKind: entry.hostKind,
      tp2AssetId: entry.tp2AssetId,
      step: 'host',
      message,
    });
    removePhotoUploadOutcomes(
      entry.photos.map((photo) => photo.photoId),
      now
    );
  });
  writeAll(
    key,
    entries.filter((entry) => !isBlocked(entry))
  );
}

/** Gate 2 result: the asset's tp2AssetId materialized (inventory). */
export function markSubmissionAssetReady(
  workTaskId: string,
  tp2AssetId: number,
  now: number = Date.now()
): void {
  updateEntry(
    (entry) => entry.workTaskId === workTaskId,
    (entry) => ({ ...entry, tp2AssetId }),
    now
  );
}

/** The create-work mutation acked; advance to the reconcile phase. */
export function markWorkCreated(workTaskId: string, now: number = Date.now()): void {
  updateEntry(
    (entry) => entry.workTaskId === workTaskId,
    (entry) => ({ ...entry, workCreated: true }),
    now
  );
}

// ── Photo upload-outcome store ─────────────────────────────────────────────────────────
// A durable, photoId-keyed record of each work-task photo upload's terminal outcome,
// written by the upload mutation when it settles. It is SEPARATE from the submission
// outbox on purpose: photos are uploaded during form-fill, often BEFORE the submission
// that references them is enqueued, so a completion signal has nowhere to land on the
// entry. Keying by photoId (which is stable and client-minted) means "uploaded before
// submit" and "uploaded after submit" are the same path — the runner just reads the
// outcome by id at reconcile time. Tenant-scoped + TTL-pruned like the outbox.

const PHOTO_OUTCOME_KEY_PREFIX = 'workPhotoUploadOutcomes';

interface StoredPhotoOutcome {
  outcome: PhotoUploadOutcome;
  at: number;
}
type PhotoOutcomeMap = Record<string, StoredPhotoOutcome>;

function photoOutcomeKey(): string {
  return `${PHOTO_OUTCOME_KEY_PREFIX}:${TenantRepository.getCurrentTenantPid() ?? 'unknown'}`;
}

function readPhotoOutcomes(key: string, now: number): PhotoOutcomeMap {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PhotoOutcomeMap;
    // Prune expired entries so the map can't grow unbounded.
    const pruned: PhotoOutcomeMap = {};
    Object.entries(parsed).forEach(([photoId, value]) => {
      if (value && now - value.at < WORK_SUBMISSION_TTL_MS) pruned[photoId] = value;
    });
    return pruned;
  } catch {
    return {};
  }
}

/** Record a work-task photo's terminal upload outcome (called headlessly by the upload
 *  mutation on success/failure — before or after its submission exists). */
export function recordPhotoUploadOutcome(
  photoId: string,
  outcome: PhotoUploadOutcome,
  now: number = Date.now()
): void {
  const key = photoOutcomeKey();
  const map = readPhotoOutcomes(key, now);
  map[photoId] = { outcome, at: now };
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // Best-effort: a full/unavailable localStorage must not break uploading.
  }
  listeners.forEach((listener) => listener());
}

/** The recorded outcome for a photo, or undefined while its upload is still in flight. */
export function getPhotoUploadOutcome(
  photoId: string,
  now: number = Date.now()
): PhotoUploadOutcome | undefined {
  return readPhotoOutcomes(photoOutcomeKey(), now)[photoId]?.outcome;
}

/** Drop the recorded outcomes for these photo ids — called when their submission settles
 *  so the store doesn't grow unbounded (the 7-day TTL is only a backstop for orphans). */
export function removePhotoUploadOutcomes(photoIds: string[], now: number = Date.now()): void {
  if (photoIds.length === 0) return;
  const key = photoOutcomeKey();
  const map = readPhotoOutcomes(key, now);
  let changed = false;
  photoIds.forEach((photoId) => {
    if (photoId in map) {
      delete map[photoId];
      changed = true;
    }
  });
  if (!changed) return;
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // Best-effort.
  }
  listeners.forEach((listener) => listener());
}

/** linkPhotosToWorkTask issued for the uploaded photos. */
export function markSubmissionPhotosLinked(workTaskId: string, now: number = Date.now()): void {
  updateEntry(
    (entry) => entry.workTaskId === workTaskId,
    (entry) => ({ ...entry, photosLinked: true }),
    now
  );
}

/** Increment the failed-online-attempt counter; the runner gives up past a max. */
export function bumpWorkSubmissionAttempts(workTaskId: string, now: number = Date.now()): void {
  updateEntry(
    (entry) => entry.workTaskId === workTaskId,
    (entry) => ({ ...entry, attempts: entry.attempts + 1 }),
    now
  );
}

/** Record the last error (for the failure toast) without removing the entry. */
export function setWorkSubmissionError(
  workTaskId: string,
  lastError: string,
  now: number = Date.now()
): void {
  updateEntry(
    (entry) => entry.workTaskId === workTaskId,
    (entry) => ({ ...entry, lastError }),
    now
  );
}

/** Drop a submission once fully reconciled (or permanently given up), cleaning up its
 *  photos' upload outcomes so neither store leaks. */
export function removeWorkSubmission(workTaskId: string, now: number = Date.now()): void {
  const key = storageKey();
  const entries = prune(readAll(key), now);
  const removed = entries.find((entry) => entry.workTaskId === workTaskId);
  if (removed) {
    removePhotoUploadOutcomes(
      removed.photos.map((photo) => photo.photoId),
      now
    );
  }
  writeAll(
    key,
    entries.filter((entry) => entry.workTaskId !== workTaskId)
  );
}

export function subscribeWorkSubmissions(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Resolves once `workTaskId` is no longer in the outbox — the runner fully reconciled
 * it — or after `timeoutMs`, or when `signal` aborts. Tenant/key captured at call time
 * so a mid-wait tenant switch can't resolve it against a different tenant's list.
 */
export function waitUntilSubmissionSettled(
  workTaskId: string,
  timeoutMs: number,
  signal?: AbortSignal
): Promise<void> {
  const key = storageKey();
  const stillPending = () =>
    submissionsForKey(key, Date.now()).some((entry) => entry.workTaskId === workTaskId);
  if (!stillPending() || signal?.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      unsubscribe();
      clearTimeout(timer);
      signal?.removeEventListener('abort', finish);
      resolve();
    };
    const unsubscribe = subscribeWorkSubmissions(() => {
      if (!stillPending()) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
    signal?.addEventListener('abort', finish, { once: true });
  });
}

// useSyncExternalStore needs a referentially-stable snapshot while unchanged; cache by
// a cheap signature (id + phase-ish flags) so React doesn't loop on a fresh array.
let cachedSignature = '';
let cachedSubmissions: WorkSubmission[] = [];
function getSnapshot(): WorkSubmission[] {
  const entries = getWorkSubmissions();
  const signature = entries
    .map(
      (e) =>
        `${e.workTaskId}:${e.hostDone ? 1 : 0}${e.workCreated ? 1 : 0}${e.photosLinked ? 1 : 0}`
    )
    .join(',');
  if (signature !== cachedSignature) {
    cachedSignature = signature;
    cachedSubmissions = entries;
  }
  return cachedSubmissions;
}

/** Reactive view of the current tenant's outbox (for indicators). */
export function useWorkSubmissions(): WorkSubmission[] {
  return useSyncExternalStore(subscribeWorkSubmissions, getSnapshot, getSnapshot);
}
